// ============================================================================
// CONFIG GROUP SIDEBAR
// ============================================================================
// Left-rail list of config groups (LITIGATION_POLICY, HEARING_POLICY, etc.).
// Active row highlighted; click → select group.

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ConfigGroup } from 'api/types/config.types';

interface ConfigGroupSidebarProps {
  groups: ConfigGroup[];
  activeGroupKey: string | null;
  onSelect: (groupKey: string) => void;
}

const ICON_MAP: Record<string, keyof typeof IconSystem> = {
  gavel: 'briefcase' as any,    // closest available
  calendar: 'calendar',
  phone: 'message',             // fallback
  bell: 'bell',
};

const resolveIcon = (raw?: string | null) => {
  if (!raw) return null;
  const key = ICON_MAP[raw] ?? (raw as keyof typeof IconSystem);
  const fn = (IconSystem as any)[key];
  return typeof fn === 'function' ? fn({ size: 16 }) : null;
};

const ConfigGroupSidebar: React.FC<ConfigGroupSidebarProps> = ({
  groups, activeGroupKey, onSelect,
}) => {
  return (
    <div style={{
      width: '240px',
      flexShrink: 0,
      borderRight: '1px solid var(--border-color)',
      background: 'var(--bg-secondary)',
      overflowY: 'auto',
      padding: '0.5rem 0',
    }}>
      {groups.length === 0 && (
        <div style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
          No config groups defined.
        </div>
      )}
      {groups.map((g) => {
        const active = g.groupKey === activeGroupKey;
        return (
          <div
            key={g.id}
            onClick={() => onSelect(g.groupKey)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(g.groupKey); } }}
            style={{
              padding: '0.625rem 1rem',
              cursor: 'pointer',
              background: active ? 'var(--bg-primary)' : 'transparent',
              borderLeft: active ? '3px solid #2563eb' : '3px solid transparent',
              fontSize: '0.875rem',
              color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
              fontWeight: active ? 600 : 500,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              userSelect: 'none',
            }}
            onMouseEnter={(e) => {
              if (!active) (e.currentTarget as HTMLElement).style.background = 'var(--bg-primary)';
            }}
            onMouseLeave={(e) => {
              if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent';
            }}
          >
            <span style={{ color: active ? '#2563eb' : 'var(--text-muted)' }}>
              {resolveIcon(g.icon)}
            </span>
            <span>{g.displayName}</span>
          </div>
        );
      })}
    </div>
  );
};

export default ConfigGroupSidebar;
